import { ISavV2 } from '../../models/savV2';
import { HybridSavListItem } from '../../models/hybridSavListItem';
import IUser from '../../models/user';
import { IListResult } from '../../models/IListResult';
import { authenticatedApi } from '../api';

/**
 * API pour les opérations core SAV V2
 */
export const savV2Api = {
  /**
   * Récupère un SAV V2 par son ID avec toutes les relations
   */
  async getById(id: number): Promise<ISavV2> {
    const response = await authenticatedApi.get(`/sav-v2/${id}`);
    return response.data;
  },

  /**
   * Récupère un SAV V2 par son identifiant unique
   */
  async getByUniqueId(uniqueId: string): Promise<ISavV2> {
    const response = await authenticatedApi.get(`/sav-v2/by-uid/${uniqueId}`);
    return response.data;
  },

  /**
   * Récupère la liste des SAV V2
   */
  async getList(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Promise<{ data: ISavV2[]; total: number; page: number; limit: number }> {
    const response = await authenticatedApi.get('/sav-v2', { params });
    return response.data;
  },

  /**
   * Récupère les SAV V2 d'un contrat
   */
  async getByContractId(contractId: number): Promise<ISavV2[]> {
    const response = await authenticatedApi.get(`/sav-v2/contract/${contractId}`);
    return response.data;
  },

  /**
   * Récupère les SAV V2 d'un bénéficiaire
   */
  async getByBeneficiaryId(beneficiaryId: number): Promise<ISavV2[]> {
    const response = await authenticatedApi.get(`/sav-v2/beneficiary/${beneficiaryId}`);
    return response.data;
  },

  /**
   * Récupère la liste simplifiée de tous les SAVs (v1 + v2) d'un contrat
   * Utilisé pour afficher l'historique des SAVs dans le panel
   */
  async getSavsByContractId(contractId: number, limit = 50): Promise<HybridSavListItem[]> {
    const response = await authenticatedApi.get(`/sav-v2/by-contract/${contractId}/list`, {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Met à jour un SAV V2
   */
  async update(id: number, data: Partial<ISavV2>): Promise<ISavV2> {
    const response = await authenticatedApi.patch(`/sav-v2/${id}`, data);
    return response.data;
  },

  /**
   * Récupère le statut virtuel d'un SAV
   */
  async getVirtualStatus(id: number): Promise<{
    id: number;
    uniqueId: string;
    actualStatus: string;
    virtualStatus: string;
    statusInfo: any;
  }> {
    const response = await authenticatedApi.get(`/sav-v2/${id}/virtual-status`);
    return response.data;
  },

  /**
   * Récupère le nombre de SAV par statut
   */
  async getCountByStatus(): Promise<Record<string, number>> {
    const response = await authenticatedApi.get('/sav-v2/count-by-status');
    return response.data;
  },

  /**
   * Récupère le nombre de SAV par statut virtuel
   */
  async getCountByVirtualStatus(): Promise<Record<string, number>> {
    const response = await authenticatedApi.get('/sav-v2/count-by-virtual-status');
    return response.data;
  },

  /**
   * Assigne un réparateur à un SAV
   */
  async assignRepairer(savId: number, repairerUuid: string, beneficiaryFetchesQuote = false): Promise<ISavV2> {
    const response = await authenticatedApi.post(`/sav-v2/${savId}/assign-repairer`, {
      repairerUuid,
      beneficiaryFetchesQuote
    });
    return response.data;
  },

  /**
   * Récupère toutes les déclarations de l'utilisateur connecté (drafts et actives)
   */
  async getMyDeclarations(): Promise<ISavV2[]> {
    const response = await authenticatedApi.get('/sav-v2/my-declarations');
    return response.data;
  },

  /**
   * Récupère l'action en attente pour le bénéficiaire sur un SAV
   */
  async getMyPendingAction(savId: number): Promise<{
    actor: string;
    type: string;
    metadata: Record<string, any>;
    createdAt: string;
  } | null> {
    const response = await authenticatedApi.get(`/sav-v2/${savId}/my-pending-action`);
    return response.data;
  },

  /**
   * Récupère le lien de signature courant pour le bénéficiaire (si la décision
   * est en PENDING_BENEFICIARY_SIGNATURE), avec refresh côté Yousign si expiré.
   */
  async getMySignatureLink(savId: number): Promise<{ signatureLink: string; refreshed: boolean }> {
    const response = await authenticatedApi.get(`/sav-v2/${savId}/my-signature-link`);
    return response.data;
  },

  /**
   * Récupère les messages d'un SAV
   */
  async getMessages(savId: number): Promise<any[]> {
    const response = await authenticatedApi.get(`/sav-v2/${savId}/messages`);
    return response.data;
  },

  /**
   * Upload un devis par le bénéficiaire suite à une action en attente
   */
  async provideQuote(
    savId: number,
    file: File,
    amount?: string,
    comments?: string,
    onProgress?: (progress: number) => void
  ): Promise<{
    success: boolean;
    quote: {
      id: string;
      documentId: number;
      status: string;
      source: string;
      autoSelected: boolean;
    };
    message: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (amount) formData.append('amount', amount);
    if (comments) formData.append('comments', comments);

    const response = await authenticatedApi.post(
      `/sav-v2/${savId}/beneficiary/provide-quote`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );

    return response.data;
  },

  /**
   * Bloque un SAV pour procédure juridique
   */
  async legalBlockSav(
    savId: number,
    data: {
      reason: string;
      letterDate: string;
      documentIds?: number[];
    }
  ): Promise<ISavV2> {
    const response = await authenticatedApi.post(`/sav-v2/${savId}/legal-block`, data);
    return response.data;
  },

  /**
   * Débloque un SAV suite à la résolution d'une procédure juridique
   */
  async legalUnblockSav(
    savId: number,
    reason: string
  ): Promise<ISavV2> {
    const response = await authenticatedApi.post(`/sav-v2/${savId}/legal-unblock`, { reason });
    return response.data;
  },

  /**
   * Marque un SAV comme "Sans Suite"
   * Ferme l'intervention courante et met le SAV en état SANS_SUITE
   */
  async markAsSansSuite(
    savId: number,
    reason: string
  ): Promise<ISavV2> {
    const response = await authenticatedApi.post(`/sav-v2/${savId}/mark-sans-suite`, { reason });
    return response.data;
  },

  /**
   * Modifier les flags d'attente d'un SAV
   */
  async updateAwaitingFlags(
    savId: number,
    flags: { awaitingBeneficiary?: boolean; awaitingManager?: boolean; awaitingGarant?: boolean }
  ): Promise<ISavV2> {
    const response = await authenticatedApi.patch(`/sav-v2/${savId}/awaiting-flags`, flags);
    return response.data;
  },

  /**
   * Récupère la liste des gestionnaires (utilisateurs avec ROLE_COMMERCIAL)
   */
  async getManagers(params?: { page?: number; limit?: number }): Promise<IListResult<IUser>> {
    const { page = 0, limit = 100 } = params || {};
    const response = await authenticatedApi.get(`/sav-v2/managers?page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Attribue ou change le gestionnaire principal d'un SAV
   */
  async assignManager(savId: number, managerUuid: string): Promise<ISavV2> {
    const response = await authenticatedApi.post(`/sav-v2/${savId}/assign-manager`, {
      managerUuid,
    });
    return response.data;
  },

  /**
   * Récupère les SAVs non attribués (sans gestionnaire)
   */
  async getUnassignedSavs(params?: {
    page?: number;
    limit?: number;
  }): Promise<IListResult<ISavV2>> {
    const response = await authenticatedApi.get('/sav-v2/unassigned', { params });
    return response.data;
  },

  /**
   * Récupère les SAVs du gestionnaire connecté
   */
  async getMySavs(params?: {
    page?: number;
    limit?: number;
  }): Promise<IListResult<ISavV2>> {
    const response = await authenticatedApi.get('/sav-v2/my-savs', { params });
    return response.data;
  },

  /**
   * Récupère une liste de SAVs avec filtres avancés
   */
  async getSavList(filters?: {
    virtualStatus?: string;
    managerFilter?: 'unassigned' | 'my-savs';
    page?: number;
    limit?: number;
  }): Promise<IListResult<ISavV2>> {
    const response = await authenticatedApi.get('/sav-v2/filtered-list', { params: filters });
    return response.data;
  },

  /**
   * Met à jour l'email du bénéficiaire associé au SAV
   */
  async updateBeneficiaryEmail(savId: number, email: string): Promise<{ success: boolean }> {
    const response = await authenticatedApi.patch(`/sav-v2/${savId}/beneficiary/email`, { email });
    return response.data;
  },

  /**
   * Récupère les counts pour les filtres SAV v2 (badges sidebar)
   */
  async getSavListCounts(): Promise<{
    unassigned: number;
    mySavs: number;
    searchingRepairer: number;
    transcribingQuote: number;
    analyzingQuote: number;
    formalizingDecision: number;
    waitingGarantSignature: number;
  }> {
    const response = await authenticatedApi.get('/sav-v2/filtered-list/counts');
    return response.data;
  },
};